import { call, put, takeLatest, take, select } from "redux-saga/effects";
import { push } from "react-router-redux";
import { delay } from "redux-saga";
import { history } from "store";

import * as fundcorpMiddleware from "middleware/fundcorp";
import * as session from "middleware/session";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { types, selectors as fundcorpSelectors } from "reducers/fundcorp";
import { actions as notificationActions } from "reducers/notification";
import { actions as formActions } from "reducers/form";
import { INSUFFICIENT_SIGNATURES, INTERNAL_ERROR, SCHEDULED_TRANSACTION, TRANSACTION_FINISHED } from "util/responses";
import { downloadPdf, downloadXls } from "util/download";
import { getProductLabel } from "util/fundcorp";
import { actions as accountsActions, types as accountsTypes, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as sessionSelectors } from "reducers/session";
import { getCurrencyIso } from "util/cbuCmf";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";

const sagas = [
    takeLatest(types.LIST_FUNDCORP_REQUEST, listFundcorp),
    takeLatest(types.LIST_FUNDCORP_PRE_REQUEST, listFundcorpPre),
    takeLatest(types.LIST_FUNDCORP_INF_PRE_REQUEST, listFundcorpInfPre),
    takeLatest(types.DOWNLOAD_LIST_FUNDCORP_REQUEST, downloadList),
    takeLatest(types.CREATE_RESCUE_REQUEST_PRE, rescueRequestPre),
    takeLatest(types.CREATE_RESCUE_REQUEST, validateRescue),
    takeLatest(types.SEND_RESCUE_REQUEST, sendRescue),
    takeLatest(types.CREATE_SUBSCRIBE_REQUEST, validateSubscribe),
    takeLatest(types.SEND_SUBSCRIBE_REQUEST, sendSubscribe),
    takeLatest(types.CREATE_SUBSCRIBE_PRE_REQUEST, subscribePreRequest),
    takeLatest(types.CREATE_SHARE_ACCOUNT_PRE_REQUEST, createShareAccountPre),
    takeLatest(types.CREATE_SHARE_ACCOUNT, createShareAccount),
    takeLatest(types.DOWNLOAD_TERMS_REQUEST, handleDownloadTerms),
    takeLatest(types.SEND_BANK_ACCOUNT_CPT_REQUEST, sendBankAccountCpt),
    takeLatest(types.INVESTOR_PROFILE_REQUEST, validateInvestorProfile),
    takeLatest(types.UPDATE_INVESTOR_PROFILE_REQUEST, updateInvestorProfile),
    takeLatest(types.CANCEL_SCHEDULED_TRANSACTION_REQUEST, cancelScheduled),
    takeLatest(types.DOWNLOAD_REPORT_DRAWER_REQUEST, downloadMonthlyReport),
    takeLatest(types.CANCEL_SUBSCRIBE_PREVIEW_REQUEST, cancelSubscribePreview),
    takeLatest(types.CANCEL_SUBSCRIBE_REQUEST, cancelSubscribe),
    takeLatest(types.FETCH_PERFORMANCE_REQUEST, fetchPerformance),
];

export default sagas;

const getMonetaryAccountByCurrency = (shareAccount, currencyIso) => {
    const monetaryAccounts = shareAccount?.monetaryAccounts;
    if (!monetaryAccounts?.length) {
        return {};
    }

    const found = monetaryAccounts.find((account) => {
        const accountCurrency = account?.monedaIso;
        if (accountCurrency === currencyIso) {
            return true;
        }
        if (account?.cbu) {
            return getCurrencyIso(account.cbu) === currencyIso;
        }
        return false;
    });

    return found || monetaryAccounts[0] || {};
};

function* listFundcorp({
    optionSelected,
    positionsType,
    positionDate,
    movementsShareAccount,
    movementsShareAccountDescription,
    movementsFund,
    movementsFundDescription,
    movementsDateFrom,
    movementsDateTo,
    requestsShareAccount,
    requestsShareAccountDescription,
    requestsDateFrom,
    requestsDateTo,
    shareValuesFund,
    shareValuesFundDescription,
    shareValuesDate,
    portfolioCompositionFund,
    portfolioCompositionFundDescription,
    portfolioCompositionDate,
    verifyCode,
    page,
}) {
    let response = null;
    if (optionSelected === "shareValues" || optionSelected === "portfolioComposition") {
        response = yield call(
            fundcorpMiddleware.listFundcorpInf,
            optionSelected,
            positionsType,
            positionDate,
            movementsShareAccount,
            movementsShareAccountDescription,
            movementsFund,
            movementsFundDescription,
            movementsDateFrom,
            movementsDateTo,
            requestsShareAccount,
            requestsShareAccountDescription,
            requestsDateFrom,
            requestsDateTo,
            shareValuesFund,
            shareValuesFundDescription,
            shareValuesDate,
            portfolioCompositionFund,
            portfolioCompositionFundDescription,
            portfolioCompositionDate,
        );
    } else {
        const filter = yield select(fundcorpSelectors.getFilter);
        response = yield call(
            fundcorpMiddleware.listFundcorp,
            optionSelected,
            positionsType,
            positionDate,
            movementsShareAccount,
            movementsShareAccountDescription,
            movementsFund,
            movementsFundDescription,
            movementsDateFrom,
            movementsDateTo,
            requestsShareAccount,
            requestsShareAccountDescription,
            requestsDateFrom,
            requestsDateTo,
            shareValuesFund,
            shareValuesFundDescription,
            shareValuesDate,
            portfolioCompositionFund,
            portfolioCompositionFundDescription,
            portfolioCompositionDate,
            verifyCode,
            page,
            filter,
        );
    }

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["fundcorp.list"]));
        yield put({ type: types.LIST_FUNDCORP_FAILURE });
    } else {
        const {
            positions,
            movements,
            requests,
            shareValues,
            portfolioComposition,
            positionsByShareAccount,
            fundFeatures,
            pendingFundRequests,
            managementReport,
            totalPages,
        } = response.data.data;
        let updatedMovements = [];
        if (optionSelected === "movementsAndRequests") {
            updatedMovements = movements.map((movement) => {
                let sharesQuantity;
                let netAmount;
                if (movement.estado.toUpperCase() === "LIQUIDADA") {
                    sharesQuantity = Math.abs(movement.liquidacionCuotapartes);
                    netAmount = movement.liquidacionImporteNeto;
                } else {
                    sharesQuantity = movement.cuotapartes !== 0 ? movement.cuotapartes : null;
                    netAmount = movement.importe !== 0 ? movement.importe : null;
                }
                return { ...movement, netAmount, sharesQuantity };
            });
        }

        yield put({
            type: types.LIST_FUNDCORP_SUCCESS,
            positionsType,
            positionDate,
            movementsShareAccount,
            movementsShareAccountDescription,
            movementsFund,
            movementsFundDescription,
            movementsDateFrom,
            movementsDateTo,
            requestsShareAccount,
            requestsShareAccountDescription,
            requestsDateFrom,
            requestsDateTo,
            shareValuesFund,
            shareValuesFundDescription,
            shareValuesDate,
            portfolioCompositionFund,
            portfolioCompositionFundDescription,
            portfolioCompositionDate,
            positions,
            positionsByShareAccount,
            pendingFundRequests: pendingFundRequests || [],
            movements: optionSelected === "movementsAndRequests" ? updatedMovements : movements,
            requests,
            shareValues,
            portfolioComposition: portfolioComposition || [],
            fundFeatures,
            managementReport: managementReport || [],
            page,
            totalPages,
        });
    }
}

function* listFundcorpInfPre({ page, defaultOption = null }) {
    const response = yield call(fundcorpMiddleware.listFundcorpInfPre, page);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["fundcorp.list"]));
        yield put({ type: types.LIST_FUNDCORP_PRE_FAILURE });
    } else {
        const {
            funds,
            shareAccounts,
            positions,
            positionsByShareAccount,
            shareValues,
            fundFeatures,
        } = response.data.data;

        const optionSelected = yield select(fundcorpSelectors.getOptionSelected);
        const option = optionSelected || defaultOption || (page === "operations" ? "positions" : "shareValues");
        yield put({
            type: types.LIST_FUNDCORP_PRE_SUCCESS,
            funds,
            shareAccounts,
            positions,
            positionsByShareAccount,
            shareValues,
            optionSelected: option,
            fundFeatures,
        });
    }
}

function* listFundcorpPre({ page, defaultOption = null }) {
    const response = yield call(fundcorpMiddleware.listFundcorpPre, page);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["fundcorp.list"]));
        yield put({ type: types.LIST_FUNDCORP_PRE_FAILURE });
    } else {
        const {
            funds,
            shareAccounts,
            positions,
            positionsByShareAccount,
            shareValues,
            fundFeatures,
            fundOrderedFeatures,
            pendingFundRequests,
        } = response.data.data;

        const optionSelected = yield select(fundcorpSelectors.getOptionSelected);
        const defaultFallback = page === "operations" ? "positions" : "shareValues";
        let option;
        if (defaultOption === "performance") {
            option = "performance";
        } else if (optionSelected && optionSelected !== "performance") {
            option = optionSelected;
        } else if (defaultOption) {
            option = defaultOption;
        } else {
            option = defaultFallback;
        }
        yield put({
            type: types.LIST_FUNDCORP_PRE_SUCCESS,
            funds,
            shareAccounts,
            positions,
            positionsByShareAccount,
            shareValues,
            optionSelected: option,
            fundFeatures,
            fundOrderedFeatures,
            pendingFundRequests,
        });
    }
}

function* downloadList({ summary, format }) {
    const { type, data } = yield call(fundcorpMiddleware.downloadList, summary, format);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_LIST_FUNDCORP_FAILURE });
        yield put(
            notificationActions.showNotification(
                i18n.get("global.unexpectedError"),
                "error",
                ["fundcorp.list"],
                null,
                false,
            ),
        );
    } else {
        yield put(
            notificationActions.showNotification(
                i18n.get("global.successDownload"),
                "success",
                ["fundcorp.list"],
                null,
                false,
            ),
        );
        yield call(delay, 3000);

        const { content, fileName } = data.data;

        if (format === "pdf") {
            downloadPdf(fileName, content);
        } else {
            downloadXls(fileName, content);
        }

        yield put({ type: types.DOWNLOAD_LIST_FUNDCORP_SUCCESS });
    }
}

function* rescueRequestPre() {
    const response = yield call(fundcorpMiddleware.rescueRequestPre);

    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["rescue"]));
    } else {
        yield put({
            type: types.CREATE_RESCUE_REQUEST_PRE_SUCCESS,
        });
    }
}

function* validateRescue({ summary, formikBag }) {
    const {
        amount,
        cuotapartes,
        rescueType,
        maxAmount,
        maxCuotapartes,
        selectedShareAccount,
        selectedFund,
        equivalentAmount,
        equivalentCuotapartes,
        creditAccountLabel,
        rescueDate,
        selectedScheduler,
    } = summary;

    const response = yield call(
        fundcorpMiddleware.validateRescueRequest,
        amount,
        cuotapartes,
        rescueType,
        maxAmount,
        maxCuotapartes,
        selectedShareAccount,
        selectedFund,
        equivalentAmount,
        equivalentCuotapartes,
        creditAccountLabel,
        rescueDate,
        selectedScheduler,
    );

    const { NO_FIELD } = response.data.data;

    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            if (response.data.data.message) {
                yield put(notificationActions.showNotification(response.data.data.message, "error", ["rescue"]));
            } else if (NO_FIELD) {
                yield put(notificationActions.showNotification(NO_FIELD, "warning", ["rescue"]));
            } else {
                formikBag.setErrors(response.data.data);
            }
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["rescue"]));
        }

        yield put({ type: types.CREATE_RESCUE_FAILURE });
    } else if (response.type === "I") {
        yield put({
            type: types.CREATE_RESCUE_SUCCESS,
            summary: { ...summary },
        });
        yield put(push("/rescue/summary"));
    } else {
        yield put({
            type: types.CREATE_RESCUE_SUCCESS,
            summary: { ...summary },
        });
        yield put(push("/rescue/summary"));
    }
}

function* sendRescue({ summary, formikBag }) {
    try {
        const response = yield call(fundcorpMiddleware.sendRescueRequest, summary);

        if (response.type === "W" && response.data.code !== INTERNAL_ERROR) {
            if (response.data.code === "COR020W") {
                formikBag.setErrors(response.data.data);
                yield put(notificationActions.showNotification(response.data.message, "error", ["rescueSummary"]));
            } else {
                yield put(notificationActions.showNotification(response.data.message, "error", ["rescueSummary"]));
            }
            yield put({ type: types.SEND_RESCUE_FAILURE });
        } else {
            yield put(
                formActions.readTransaction({
                    pathname: `/transaction/${response.data.data.idTransaction}`,
                    newTransaction: true,
                }),
            );
            yield take("READ_TRANSACTION_REQUEST"); // waiting until finish action

            const { data } = response;
            const { code } = data;

            if (code !== INTERNAL_ERROR && code !== INSUFFICIENT_SIGNATURES) {
                if (code === TRANSACTION_FINISHED) {
                    yield put(
                        notificationActions.showNotification(i18n.get("transfers.success.message"), "success", [
                            "transferTransaction",
                            "pendingTransfer",
                        ]),
                    );
                } else if (code === SCHEDULED_TRANSACTION) {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("transfers.schedule.success.message"),
                            "success",
                            ["transactionScheduled"],
                        ),
                    );
                } else if (summary.idTransaction !== null) {
                    yield put(
                        notificationActions.showNotification(i18n.get("transfers.modify.success.message"), "success", [
                            "transferTransaction",
                            "pendingTransfer",
                        ]),
                    );
                }
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* validateSubscribe({ summary, formikBag }) {
    const {
        fundName,
        selectedFund,
        amount,
        debitAccontLabel,
        debitAccountId,
        shareAccount,
        subscribeDate,
        selectedScheduler,
    } = summary;

    const response = yield call(
        fundcorpMiddleware.validateSubscribeRequest,
        fundName,
        selectedFund,
        amount,
        debitAccontLabel,
        debitAccountId,
        shareAccount,
        subscribeDate,
        selectedScheduler,
    );

    const { NO_FIELD } = response.data.data;
    const { fondoClase } = response.data.data;

    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            if (response.data.data.message) {
                yield put(notificationActions.showNotification(response.data.data.message, "error", ["subscribe"]));
            } else if (NO_FIELD) {
                yield put(notificationActions.showNotification(NO_FIELD, "warning", ["subscribe"]));
            } else {
                formikBag.setErrors(response.data.data);
            }
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["subscribe"]));
        }

        yield put({ type: types.CREATE_SUBSCRIBE_FAILURE });
    } else if (response.type === "I") {
        yield put({
            type: types.CREATE_SUBSCRIBE_SUCCESS,
            summary: { ...summary, fondoClase },
        });
        yield put(push("/subscribe/summary"));
    } else {
        yield put({
            type: types.CREATE_SUBSCRIBE_SUCCESS,
            summary: { ...summary, fondoClase },
        });
        yield put(push("/subscribe/summary"));
    }
}

function* sendSubscribe({ summary, formikBag }) {
    try {
        const response = yield call(fundcorpMiddleware.sendSubscribeRequest, summary);

        if (response.type === "W" && response.data.code !== INTERNAL_ERROR) {
            if (response.data.code === "COR020W") {
                formikBag.setErrors(response.data.data);
            } else {
                yield put(notificationActions.showNotification(response.data.message, "error", ["subscribeSummary"]));
            }
            yield put({ type: types.SEND_SUBSCRIBE_FAILURE });
        } else {
            yield put(formActions.readTransaction({ pathname: `/transaction/${response.data.data.idTransaction}` }));
            yield take("READ_TRANSACTION_REQUEST"); // waiting until finish action

            const { data } = response;
            const { code } = data;

            if (code !== INTERNAL_ERROR && code !== INSUFFICIENT_SIGNATURES) {
                if (code === TRANSACTION_FINISHED) {
                    yield put(
                        notificationActions.showNotification(i18n.get("transfers.success.message"), "success", [
                            "transferTransaction",
                            "pendingTransfer",
                        ]),
                    );
                } else if (code === SCHEDULED_TRANSACTION) {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("transfers.schedule.success.message"),
                            "success",
                            ["transactionScheduled"],
                        ),
                    );
                } else if (summary.idTransaction !== null) {
                    yield put(
                        notificationActions.showNotification(i18n.get("transfers.modify.success.message"), "success", [
                            "transferTransaction",
                            "pendingTransfer",
                        ]),
                    );
                }
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* subscribePreRequest() {
    const response = yield call(fundcorpMiddleware.subscribePreRequest);

    if (response.type === "W") {
        yield put({ type: types.CREATE_SUBSCRIBE_PRE_FAILURE });
    } else {
        yield put({
            type: types.CREATE_SUBSCRIBE_PRE_SUCCESS,
        });
    }
}

function* createShareAccountPre() {
    const response = yield call(fundcorpMiddleware.createShareAccountPreRequest);

    if (response.type === "W") {
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                "registerCuotapartista",
            ]),
        );
        yield put({ type: types.CREATE_SHARE_ACCOUNT_PRE_FAILURE });
    } else {
        const { shareAccounts, investorProfile } = response.data.data;

        yield put({
            type: types.CREATE_SHARE_ACCOUNT_PRE_SUCCESS,
            shareAccounts,
            investorProfile,
        });
    }
}

function* createShareAccount({ summaryShareaccount, formikBag }) {
    try {
        const response = yield call(fundcorpMiddleware.createShareAccountRequest, summaryShareaccount);
        const { NO_FIELD } = response.data.data;

        if (response.type === "W") {
            if (response.data.code === "COR020W") {
                if (response.data.data.message) {
                    yield put(
                        notificationActions.showNotification(response.data.data.message, "error", [
                            "registerCuotapartista",
                        ]),
                    );
                } else if (NO_FIELD) {
                    yield put(notificationActions.showNotification(NO_FIELD, "warning", ["registerCuotapartista"]));
                } else {
                    formikBag.setErrors(response.data.data);
                }
            } else {
                yield put(
                    notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                        "registerCuotapartista",
                    ]),
                );
            }

            yield put({ type: types.CREATE_SHARE_ACCOUNT_FAILURE });
        } else {
            const { shareAccounts, numCuotapartista, status } = response.data.data;
            yield put({
                type: types.CREATE_SHARE_ACCOUNT_SUCCESS,
                shareAccounts,
                summaryShareaccount: { ...summaryShareaccount },
                statusRegisterShareAccount: status,
                numCuotapartista,
            });
            if (status === "FINISHED") {
                yield put(push("/registerShareAccount/success"));
            } else {
                yield put(push("/registerShareAccount/failed"));
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* handleDownloadTerms({ format, configVar }) {
    const { type, data } = yield call(session.exportTermsAndConditionsConfigVar, format, configVar);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_TERMS_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["settings"]));
    } else {
        const { content, fileName } = data.data;
        downloadPdf(fileName, content);
        yield put({ type: types.DOWNLOAD_TERMS_SUCCESS });
    }
}

function* sendBankAccountCpt({ debitAccount, shareAccount, formikBag }) {
    try {
        const response = yield call(fundcorpMiddleware.insertBankAccountCptRequest, debitAccount, shareAccount);
        const { NO_FIELD } = response.data.data;

        if (response.type === "W") {
            if (response.data.code === "COR020W") {
                if (response.data.data.message) {
                    yield put(notificationActions.showNotification(response.data.data.message, "error", ["subscribe"]));
                } else if (NO_FIELD) {
                    yield put(notificationActions.showNotification(NO_FIELD, "warning", ["subscribe"]));
                } else {
                    formikBag.setErrors(response.data.data);
                }
            } else {
                yield put(
                    notificationActions.showNotification(
                        i18n.get("fund.operation.associateShareAccount.error"),
                        "error",
                        ["subscribe"],
                    ),
                );
            }

            yield put({ type: types.SEND_BANK_ACCOUNT_CPT_FAILURE });
        } else {
            const { shareAccounts, status } = response.data.data;

            if (status === "FINISHED") {
                yield put({ type: types.SEND_BANK_ACCOUNT_CPT_SUCCESS, shareAccounts });
                yield put(
                    notificationActions.showNotification(
                        i18n.get("fund.operation.associateShareAccount.success"),
                        "success",
                        ["subscribe"],
                    ),
                );
            } else {
                yield put({ type: types.SEND_BANK_ACCOUNT_CPT_FAILURE });
                yield put(
                    notificationActions.showNotification(
                        i18n.get("fund.operation.associateShareAccount.error"),
                        "error",
                        ["subscribe"],
                    ),
                );
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* validateInvestorProfile({ redirectionUrl, returnPath }) {
    const response = yield call(fundcorpMiddleware.createShareAccountPreRequest);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["fundcorp.list"]));
        yield put({ type: types.INVESTOR_PROFILE_FAILURE });
    } else {
        const { shareAccounts, investorProfile } = response.data.data;

        yield put({
            type: types.RESET_SUMMARY,
        });
        yield put({
            type: types.INVESTOR_PROFILE_SUCCESS,
            investorProfile,
        });

        if (
            !investorProfile.valid &&
            (Boolean(!investorProfile?.investorProfileType.trim()) || Boolean(!investorProfile?.lastUpdate.trim()))
        ) {
            yield put(push({ pathname: "/fundcorpOperations/investor-profile", state: { from: redirectionUrl } }));
        } else if (!investorProfile.valid) {
            yield put(
                notificationActions.showNotification(i18n.get("fundcorp.investorProfile.expired"), "warning", [
                    "fundcorp.investorProfile",
                ]),
            );
            yield put(push("/fundcorpOperations/investor-profile"));
            yield put(push({ pathname: "/fundcorpOperations/investor-profile", state: { from: redirectionUrl } }));
        } else if (shareAccounts.length <= 0) {
            yield put(push("/fundcorpOperations/registerShareAccount"));
        } else {
            yield put(push({ pathname: redirectionUrl, state: returnPath ? { return: returnPath } : undefined }));
        }
    }
}

function* updateInvestorProfile({ investorProfile }) {
    const { location } = history;
    const shareAccounts = yield select(fundcorpSelectors.getShareAccounts);

    const response = yield call(fundcorpMiddleware.updateInvestorProfile, investorProfile);

    if (response.type === "W") {
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                "fundcorp.investorProfile",
            ]),
        );
        yield put({ type: types.UPDATE_INVESTOR_PROFILE_FAILURE });
    } else {
        const { investorProfile: investorProfileResponse } = response.data.data;
        yield put({
            type: types.UPDATE_INVESTOR_PROFILE_SUCCESS,
            investorProfile: investorProfileResponse,
        });

        yield put(
            notificationActions.showNotification(
                `Actualizaste tu perfil de inversor a ${i18n.get(
                    `fundcorp.investorProfile.${investorProfileResponse.investorProfileType}.title`,
                )}.`,
                "success",
                ["fundcorp.investorProfile"],
            ),
        );
        if (location?.state?.from && shareAccounts.length <= 0) {
            yield put(
                notificationActions.showNotification(
                    `Actualizaste tu perfil de inversor a ${i18n.get(
                        `fundcorp.investorProfile.${investorProfileResponse.investorProfileType}.title`,
                    )}.`,
                    "success",
                    ["registerCuotapartista"],
                ),
            );
            yield put(
                push({ pathname: "/fundcorpOperations/registerShareAccount", state: { from: location.state.from } }),
            );
        } else if (location?.state?.from) {
            yield put(
                notificationActions.showNotification(
                    `Actualizaste tu perfil de inversor a ${i18n.get(
                        `fundcorp.investorProfile.${investorProfileResponse.investorProfileType}.title`,
                    )}.`,
                    "success",
                    ["subscribe"],
                ),
            );
            yield put(push({ pathname: location.state.from, state: { from: location.state.from } }));
        }
    }
}

function* cancelScheduled({ summary }) {
    const { formikBag, idTransaction } = summary;
    const response = yield call(fundcorpMiddleware.cancelScheduledTransaction, summary);

    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            formikBag.setErrors(response.data.data);
        } else {
            yield put(
                notificationActions.showNotification(response.data.message, "error", ["transactionScheduledCancel"]),
            );
        }
        yield put({ type: types.CANCEL_SCHEDULED_TRANSACTION_FAILURE });
    } else {
        yield put(
            formActions.readTransaction({
                pathname: `/transaction/${response.data.data?.idTransactionToCancel || response.data.idTransaction}`,
            }),
        );

        const { data } = response;
        const { code } = data;

        if (code === SCHEDULED_TRANSACTION) {
            yield put(
                notificationActions.showNotification(i18n.get("scheduler.cancel.success"), "success", [
                    "transactionScheduled",
                ]),
            );
        } else if (code !== INSUFFICIENT_SIGNATURES && idTransaction !== null) {
            yield put(
                notificationActions.showNotification(i18n.get("scheduler.cancel.success"), "success", [
                    "pendingTransfer",
                ]),
            );
        }

        yield put({
            type: types.CANCEL_SCHEDULED_TRANSACTION_SUCCESS,
            idTransaction,
        });
    }
}

function* downloadMonthlyReport({ reportDrawerValues, format }) {
    const { type, data } = yield call(fundcorpMiddleware.downloadMonthlyReport, reportDrawerValues, format);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_REPORT_DRAWER_FAILURE });
        yield put(
            notificationActions.showNotification(
                i18n.get("global.unexpectedError"),
                "error",
                !isUxDashboardImprovementsEnabled()
                    ? ["monthlyReportDrawer"]
                    : ["monthlyReportDrawer", "fundcorp.list"],
            ),
        );
    } else {
        yield call(delay, 3000);

        const { content, fileName } = data.data;

        if (format.toUpperCase() === "PDF") {
            downloadPdf(fileName, content);
        } else {
            downloadXls(fileName, content);
        }

        yield put(
            notificationActions.showNotification(
                i18n.get("global.successDownload"),
                "success",
                !isUxDashboardImprovementsEnabled()
                    ? ["monthlyReportDrawer"]
                    : ["monthlyReportDrawer", "fundcorp.list"],
                null,
                false,
            ),
        );
        yield put({ type: types.DOWNLOAD_REPORT_DRAWER_SUCCESS });
    }
}

function* cancelSubscribePreview({ fundItem }) {
    const cancelableText = configUtils.get(
        "frontend.investment.funds.cancelable.text",
        i18n.get("fund.cancel.outOfSchedule"),
    );

    const isWithinSchedule = configUtils.isWithinCancelSchedule();

    if (!isWithinSchedule) {
        yield put(notificationActions.showNotification(cancelableText, "warning", ["fundcorp.list"]));
        yield put({ type: types.CANCEL_SUBSCRIBE_PREVIEW_FAILURE });
        return;
    }
    const loggedUser = yield select(sessionSelectors.getUser);
    const activeEnvironment = yield select(sessionSelectors.getActiveEnvironment);
    let accounts = yield select(accountsSelectors.getAccounts);
    const canListAccounts = !!activeEnvironment?.permissions?.accounts;

    if (canListAccounts && (!accounts || accounts.length === 0)) {
        yield put(accountsActions.listAccounts(loggedUser.userId));
        yield take([accountsTypes.LIST_ACCOUNTS_SUCCESS, accountsTypes.LIST_ACCOUNTS_FAILURE]);
        accounts = yield select(accountsSelectors.getAccounts);
    }
    const shareAccounts = yield select(fundcorpSelectors.getShareAccounts);
    const shareAccount = shareAccounts.find((sa) => sa.id?.toString() === fundItem?.cuotapartistaNumero?.toString());
    const fundCurrency = fundItem?.monedaIso;

    const monetaryAccount = getMonetaryAccountByCurrency(shareAccount, fundCurrency);
    const ibsAccount = accounts.find((account) => account?.cbu && account.cbu === monetaryAccount?.cbu);
    const creditAccountId = ibsAccount?.idProduct || "";
    const creditAccountLabel = getProductLabel(ibsAccount);

    const summary = {
        fondoNombre: fundItem?.fondoNombre,
        fondoClase: fundItem.tipoValorCuotaparteDescripcion,
        cuotapartistaNumero: fundItem?.cuotapartistaNumero,
        fondoNumero: fundItem?.fondoNumero,
        fechaConcertacion: fundItem?.fechaConcertacion,
        amountToShow: { currency: fundItem?.monedaIso, quantity: Number.parseFloat(fundItem?.importe) || 0 },
        creditAccountLabel,
        creditAccountId,
        idSolicitud: fundItem?.idSolicitud,
    };

    const response = yield call(fundcorpMiddleware.cancelSubscribePreview, summary);

    if (response.type === "W") {
        const validationKey = response.data?.data?.NO_FIELD;
        const fallbackMessage = response.data?.message || i18n.get("global.unexpectedError");
        const messageKey = validationKey || "frontend.investment.funds.cancelable.text";
        const displayMessage = configUtils.get(messageKey, fallbackMessage);
        yield put(notificationActions.showNotification(displayMessage, "warning", ["fundcorp.list"]));
        yield put({ type: types.CANCEL_SUBSCRIBE_PREVIEW_FAILURE });
    } else {
        const responseData = response.data?.data || {};
        yield put({
            type: types.CANCEL_SUBSCRIBE_PREVIEW_SUCCESS,
            summary: {
                ...summary,
                isWithinSchedule:
                    responseData.isWithinSchedule !== undefined ? responseData.isWithinSchedule : isWithinSchedule,
                cancelDate: responseData.cancelDate,
            },
        });
        yield put(push("/cancelSubscribe/summary"));
    }
}

function* cancelSubscribe({ summary }) {
    const response = yield call(fundcorpMiddleware.cancelSubscribe, summary);

    if (response.type === "W" && response.data.code !== INTERNAL_ERROR) {
        const validationKey = response.data?.data?.NO_FIELD;
        const fallbackMessage = response.data?.message || i18n.get("global.unexpectedError");
        const messageKey = validationKey || "frontend.investment.funds.cancelable.text";
        const errorText = configUtils.get(messageKey, fallbackMessage);
        yield put(notificationActions.showNotification(errorText, "error", ["cancelFound"]));
        yield put({ type: types.CANCEL_SUBSCRIBE_FAILURE, summary });
    } else {
        yield put(
            formActions.readTransaction({
                pathname: `/transaction/${response.data.data.idTransaction}`,
                newTransaction: true,
            }),
        );
        yield put({ type: types.CANCEL_SUBSCRIBE_SUCCESS, summary });
        yield take("READ_TRANSACTION_REQUEST");
    }
}

function* fetchPerformance({ shareAccountId, fundId, abreviatura, viewType, verifyCode, dateFrom, dateTo }) {
    const response = yield call(
        fundcorpMiddleware.fetchPerformance,
        shareAccountId,
        fundId,
        abreviatura,
        viewType,
        verifyCode,
        dateFrom,
        dateTo,
    );
    if (response.type === "W") {
        yield put({ type: types.FETCH_PERFORMANCE_FAILURE });
    } else {
        const { series, kpis, firstSubscriptionDate, chartSlots, chartSlotStyle, lastBusinessDay } = response.data.data;
        yield put({
            type: types.FETCH_PERFORMANCE_SUCCESS,
            series: series || [],
            chartSlots: chartSlots || [],
            chartSlotStyle: chartSlotStyle || "DAILY",
            lastBusinessDay: lastBusinessDay || null,
            kpis: kpis || {},
            firstSubscriptionDate: firstSubscriptionDate || null,
        });
    }
}
